import { createContext, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode, type Ref } from 'react';
import { PageContextProvider } from '@wix/data-tools-generic-events';
import { Spinner } from '../../Spinner';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, ChevronDown, Link2, X } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useMyAgents } from '../../../lib/my-agents';
import { useWorkspaces } from '../../../lib/workspaces';
import { presentWebsite, websiteDisplayName, websiteHost, type WorkspaceWebsite } from '../../../lib/workspace-website';
import { queryKeys } from '../../../lib/query-client';
import { connectorLogoUrl, fetchConnectors } from '../../../lib/connectors';
import { openConnectWalk } from '../../../lib/connect-walk-popup';
import { useConnectWalkPopupEnabled } from '../../../lib/connect-walk-popup-flag';
import { bandConnectUrlFrom, bandOptionFields, connectedToolkitsOf, type BandOfferCardData, type BandOfferCardOptionData, type BandPick } from '../../../lib/band-cards';
import { findGoalForCard, type GoalCardData } from '../../../lib/goal-card';
import { useWorkspaceGoals } from '../../../lib/goals';
import { awaitsDecision, showsLiveMarker } from '../../../lib/agent-task-buckets';
import {
  BUSINESS_KIND_KEYS,
  valueFirstPickState,
  claimConfetti,
  businessCardCopy,
  businessCardEditText,
  evidenceHasWixMark,
  goalHintKey,
  connectStepUrl,
  liveTaskState,
  LIVE_TASK_LABEL_KEYS,
  teamCardSubtitleKey,
  valueFirstTapAnswer,
  type ValueFirstTapAnswer,
  type BusinessCardData,
  type ConnectMeanwhileCardData,
  type TeamCardData,
  type LiveTaskState,
} from '../../../lib/value-first-onboarding';
import {
  VALUE_FIRST_CLICKED,
  VALUE_FIRST_COMPONENT,
  VALUE_FIRST_PAGE_CONTEXT,
  businessCardSeenFields,
  businessChipId,
  businessQuestionSeenFields,
  connectSeenFields,
  goalCardSeenFields,
  goalQuestionSeenFields,
  sitePickerSeenFields,
  teamCardSeenFields,
} from '../../../lib/value-first-bi';
import { useValueFirstSeenBi } from '../../../lib/use-value-first-seen-bi';
import { AgentDot } from '../../AgentDot';
import { Markdown } from '../../Markdown';
import { Badge, Button, Card, IconButton, Input, TextButton, Typography } from '../ui';
import { fireConfettiRain } from '../confetti';
import { CHAT_HOME_BI } from '../chat-surface/message-utils';
import styles from './ValueFirstChat.module.css';

// The value-first onboarding's chat (native-apps/shared/onboarding-flow): the title above the chat, the docked
// question cards above the composer, and the answer cards in the transcript. Drawn only for a value-first
// conversation; MessagesPanel decides where each piece goes, these only draw it.

const TITLE_DOT_IDS = ['value-first-1', 'value-first-2', 'value-first-3'];
/** "Not now" as a pick: every goal option then waits. */
const NOT_NOW_PICK = 'notNow';
const SKIP_PICK = 'skip';

type ValueFirstBiScopeValue = { conversationId: string | null; step: string | null; visible: boolean };

const ValueFirstBiContext = createContext<ValueFirstBiScopeValue>({ conversationId: null, step: null, visible: false });

/**
 * Around every value-first piece MessagesPanel draws: their events report the chat as the page (whatever route the
 * chat sits on, as the phones do), and read the conversation, the step and whether the chat surface is open.
 */
export function ValueFirstBiScope({ children, conversationId, step, visible }: ValueFirstBiScopeValue & { children: ReactNode }) {
  const scope = useMemo(() => ({ conversationId, step, visible }), [conversationId, step, visible]);
  return (
    <PageContextProvider pageContext={VALUE_FIRST_PAGE_CONTEXT}>
      <ValueFirstBiContext.Provider value={scope}>{children}</ValueFirstBiContext.Provider>
    </PageContextProvider>
  );
}

type PageClick = ReturnType<typeof useGenericBi>['logPageClickBi'];

/**
 * Hands MessagesPanel a click logger that reports the chat page, for the value-first clicks it fires itself (the goal
 * pick, the task survey's submit and reopen): its own logger sits above every `ValueFirstBiScope`.
 */
export function ValueFirstChatPageLogger({ logRef }: { logRef: MutableRefObject<PageClick | null> }) {
  return (
    <PageContextProvider pageContext={VALUE_FIRST_PAGE_CONTEXT}>
      <ChatPageLogger logRef={logRef} />
    </PageContextProvider>
  );
}

function ChatPageLogger({ logRef }: { logRef: MutableRefObject<PageClick | null> }) {
  const { logPageClickBi } = useGenericBi('composer');
  useEffect(() => {
    logRef.current = logPageClickBi;
    return () => {
      logRef.current = null;
    };
  }, [logRef, logPageClickBi]);
  return null;
}

/**
 * The `itemSeen` for a value-first piece (lib/value-first-bi). `key` tells two pieces of one component apart, from
 * what the piece shows rather than its row id: a streamed row is drawn under a local id until it is saved.
 */
function useSeen(component: string, key: string | undefined, fields: Record<string, string>) {
  const { conversationId, step, visible } = useContext(ValueFirstBiContext);
  return useValueFirstSeenBi(component, conversationId, key, { ...fields, onboarding_step: step ?? undefined }, visible);
}

/**
 * A tap on a value-first piece: the src:172 `button_name` as before, and on the generic click the piece's component
 * with `clicked` naming the tap.
 */
function useValueFirstClick(conversationId: string | null | undefined, component: string) {
  const { logPageClickBi } = useGenericBi();
  const { step } = useContext(ValueFirstBiContext);
  return (buttonName: string, clicked: string, additionalFields?: Record<string, string | undefined>) =>
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: buttonName, ...(conversationId ? { conversation_id: conversationId } : {}) },
      {
        componentName: component,
        additionalFields: { ...additionalFields, clicked, onboarding_step: step ?? undefined, conversation_id: conversationId ?? undefined },
      },
    );
}

/** A tap held while the reply runs: set on tap, cleared when the dock is free again. */
function useValueFirstPick(busy: boolean): [string | null, (id: string) => void] {
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => {
    if (!busy) setPicked(null);
  }, [busy]);
  return [picked, setPicked];
}

/** The X on a docked question (contract `layout.skip`): it skips that step, so it is a tap, not a close. */
function SkipButton({ disabled, onSkip }: { disabled: boolean; onSkip: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton size="small" aria-label={t('valueFirst.skip')} title={t('valueFirst.skip')} disabled={disabled} onClick={onSkip}>
      <X size={16} aria-hidden />
    </IconButton>
  );
}

/** The heading above the chat: three agent dots and the step's title. */
export function ValueFirstTitle({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation();
  const agentName = useAgentName();
  return (
    <div className={styles.title} data-testid="value-first-title">
      <span className={styles.titleDots} aria-hidden>
        {TITLE_DOT_IDS.map((id) => (
          <span key={id} className={styles.titleDot}>
            <AgentDot id={id} size={36} />
          </span>
        ))}
      </span>
      <Typography variant="h1" component="h2" className={styles.titleText}>
        {t(titleKey, { maestroAgentName: agentName.name })}
      </Typography>
    </div>
  );
}

/**
 * The docked question at `business`: a chip sends its label as the owner's message, and "Look at my site or store
 * first" swaps the card for the site picker (the owner's Wix sites, or a pasted link).
 */
export function BusinessQuestionDock({
  conversationId,
  disabled,
  onSend,
  picking,
  setPicking,
}: {
  conversationId: string | null;
  disabled: boolean;
  onSend: (text: string, answer: ValueFirstTapAnswer) => void;
  /** The site picker is open; held by the panel, because it also changes the composer's placeholder. */
  picking: boolean;
  setPicking: (picking: boolean) => void;
}) {
  if (picking) {
    return <SitePickerDock conversationId={conversationId} disabled={disabled} onSend={onSend} setPicking={setPicking} />;
  }
  return <BusinessQuestion conversationId={conversationId} disabled={disabled} onSend={onSend} setPicking={setPicking} />;
}

function SitePickerDock({
  conversationId,
  disabled,
  onSend,
  setPicking,
}: {
  conversationId: string | null;
  disabled: boolean;
  onSend: (text: string, answer: ValueFirstTapAnswer) => void;
  setPicking: (picking: boolean) => void;
}) {
  const { t } = useTranslation();
  const click = useValueFirstClick(conversationId, VALUE_FIRST_COMPONENT.websiteOrSocialLink);
  return (
    <SitePicker
      disabled={disabled}
      onBack={() => {
        click('value_first_site_back', VALUE_FIRST_CLICKED.back);
        setPicking(false);
      }}
      onRead={(url, via, metaSiteId) => {
        click('value_first_site_read', via === 'wix_site' ? VALUE_FIRST_CLICKED.wixSite : VALUE_FIRST_CLICKED.link, { via, site_id: metaSiteId });
        setPicking(false);
        onSend(t('valueFirst.site.readMessage', { url }), valueFirstTapAnswer({ kind: 'site', metaSiteId }));
      }}
    />
  );
}

function BusinessQuestion({
  conversationId,
  disabled,
  onSend,
  setPicking,
}: {
  conversationId: string | null;
  disabled: boolean;
  onSend: (text: string, answer: ValueFirstTapAnswer) => void;
  setPicking: (picking: boolean) => void;
}) {
  const { t } = useTranslation();
  const click = useValueFirstClick(conversationId, VALUE_FIRST_COMPONENT.aboutTheBusiness);
  const seenRef = useSeen(VALUE_FIRST_COMPONENT.aboutTheBusiness, 'dock', businessQuestionSeenFields(BUSINESS_KIND_KEYS));
  const [picked, setPicked] = useValueFirstPick(disabled);
  return (
    <Card ref={seenRef} className={styles.dock} data-testid="value-first-business-dock">
      <div className={styles.dockHeadRow}>
        <div className={styles.dockHead}>
          <Typography variant="h4" component="h3" className={styles.dockQuestion}>
            {t('valueFirst.business.question')}
          </Typography>
          <Typography variant="p3" color="secondary">
            {t('valueFirst.business.hint')}
          </Typography>
        </div>
        <SkipButton
          disabled={disabled || picked !== null}
          onSkip={() => {
            click('value_first_skip', VALUE_FIRST_CLICKED.skip, { question: 'business' });
            setPicked(SKIP_PICK);
            onSend(t('valueFirst.skip'), valueFirstTapAnswer({ kind: 'skip', question: 'business' }));
          }}
        />
      </div>
      <div className={styles.chips}>
        <Button
          variant="outlined"
          size="small"
          disabled={disabled || picked !== null}
          startIcon={<Link2 size={16} aria-hidden className={styles.chipIcon} />}
          onClick={() => {
            click('value_first_look_at_site', VALUE_FIRST_CLICKED.lookAtSite);
            setPicking(true);
          }}
        >
          {t('valueFirst.business.lookAtSite')}
        </Button>
        {BUSINESS_KIND_KEYS.map((key) => {
          const state = valueFirstPickState(key, picked, disabled);
          return (
            <Button
              key={key}
              variant={state === 'picked' ? 'contained' : 'outlined'}
              size="small"
              disabled={state === 'waiting'}
              aria-pressed={state === 'picked'}
              startIcon={state === 'picked' ? <Check size={16} aria-hidden /> : undefined}
              onClick={() => {
                if (state !== 'idle') return;
                click('value_first_business_chip', VALUE_FIRST_CLICKED.businessChip, { chip: businessChipId(key) });
                setPicked(key);
                onSend(t(key), valueFirstTapAnswer({ kind: 'business', chipKey: key }));
              }}
            >
              {t(key)}
            </Button>
          );
        })}
      </div>
    </Card>
  );
}

function ownerSites(workspaces: { website?: WorkspaceWebsite }[]): WorkspaceWebsite[] {
  const seen = new Set<string>();
  return workspaces.flatMap((workspace) => {
    const site = presentWebsite(workspace.website);
    const key = site ? site.metaSiteId || site.url || '' : '';
    if (!site || !key || seen.has(key)) return [];
    seen.add(key);
    return [site];
  });
}

function SitePicker({
  disabled,
  onBack,
  onRead,
}: {
  disabled: boolean;
  onBack: () => void;
  onRead: (url: string, via: 'wix_site' | 'link', metaSiteId?: string) => void;
}) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const sites = useMemo(() => (workspaces.status === 'ready' ? ownerSites(workspaces.workspaces) : []), [workspaces]);
  const seenRef = useSeen(VALUE_FIRST_COMPONENT.websiteOrSocialLink, 'dock', sitePickerSeenFields(sites));
  const [link, setLink] = useState('');
  const submitLink = () => {
    const url = link.trim();
    if (!url || disabled) return;
    onRead(url, 'link');
  };
  return (
    <Card ref={seenRef} className={styles.dock} data-testid="value-first-site-picker">
      <div className={styles.dockHeadRow}>
        <div className={styles.dockHead}>
          <Typography variant="h4" component="h3" className={styles.dockQuestion}>
            {t('valueFirst.site.question')}
          </Typography>
          <Typography variant="p3" color="secondary">
            {t('valueFirst.site.hint')}
          </Typography>
        </div>
        <TextButton size="small" underline onClick={onBack}>
          {t('valueFirst.site.back')}
        </TextButton>
      </div>
      {sites.length > 0 && (
        <div className={styles.sites}>
          <Typography variant="tiny-semi-bold" color="secondary" className={styles.sitesLabel}>
            {t('valueFirst.site.yourSites')}
          </Typography>
          {sites.map((site) => {
            const host = websiteHost(site.url);
            const name = websiteDisplayName(site);
            return (
              <Card
                key={site.metaSiteId || site.url}
                component="button"
                variant="stone"
                size="sm"
                interactive
                className={styles.siteRow}
                aria-disabled={disabled || undefined}
                onClick={() => {
                  if (!disabled) onRead(site.url?.trim() || name, 'wix_site', site.metaSiteId);
                }}
              >
                <span className={styles.siteText}>
                  <span className={styles.siteName}>{host || name}</span>
                  {host && name !== host ? <span className={styles.siteSub}>{name}</span> : null}
                </span>
                <ArrowRight size={20} aria-hidden className="dir-flip" />
              </Card>
            );
          })}
          <div className={styles.orPaste}>
            <span>{t('valueFirst.site.orPaste')}</span>
          </div>
        </div>
      )}
      <form
        className={styles.linkRow}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          submitLink();
        }}
      >
        <Input
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder={t('valueFirst.site.linkPlaceholder')}
          aria-label={t('valueFirst.site.linkPlaceholder')}
          inputMode="url"
          startIcon={<Link2 size={16} aria-hidden className={styles.chipIcon} />}
          endAdornment={
            <Button type="submit" size="small" disabled={disabled || !link.trim()}>
              {t('valueFirst.site.readIt')}
            </Button>
          }
        />
      </form>
    </Card>
  );
}

/** The site being read, until its business card arrives: a spinner and the site, nothing else in v1. */
export function ReadingSiteDock({ site }: { site: string }) {
  const { t } = useTranslation();
  return (
    <Card className={styles.dock} data-testid="value-first-reading" role="status">
      <div className={styles.reading}>
        <Spinner size={16} />
        <Typography variant="h4" component="p" className={styles.dockQuestion}>
          {t('valueFirst.site.reading', { site })}
        </Typography>
      </div>
    </Card>
  );
}

/** Holds the docked card above the composer; each new card slides in, so a question does not just pop over the chat. */
export function ValueFirstDockMotion({ children }: { children: ReactNode }) {
  return <div className={styles.dockMotion}>{children}</div>;
}

/** Maestro's words when they are the whole question: drawn as the docked card, never as a loose bubble. */
export function DockedProse({ text }: { text: string }) {
  return (
    <Card className={styles.dock} data-testid="value-first-docked-prose">
      <div className={styles.prose} dir="auto">
        <Markdown variant="chat">{text}</Markdown>
      </div>
    </Card>
  );
}

function OfferAgents({ option }: { option: BandOfferCardOptionData }) {
  return (
    <div className={styles.offerTeam}>
      <span className={styles.offerDots} aria-hidden>
        {option.agents.slice(0, 3).map((agent, i) => (
          <span key={`${agent.name}-${i}`} className={styles.offerDot}>
            <AgentDot id={agent.id ?? agent.name} size={20} />
          </span>
        ))}
      </span>
      {option.tagline ? <span className={styles.offerTagline}>{option.tagline}</span> : null}
      <span className={styles.srOnly}>{option.agents.map((agent) => agent.name).join(', ')}</span>
    </div>
  );
}

/**
 * The goal question, docked: one column per option on a wide chat and stacked on a phone, "Best fit" on the
 * option the server picked, its evidence line, the goal line and the team. "Not now, just help me with one thing"
 * sends its own label.
 */
export function BandOfferDock({
  card,
  conversationId,
  disabled,
  onPick,
  onSend,
  messageId,
  businessFromWixPack = false,
}: {
  card: BandOfferCardData;
  conversationId: string | null;
  disabled: boolean;
  /** The chat's business card came from the Wix pack: the hint says "I know your site". */
  businessFromWixPack?: boolean;
  onPick: (option: BandPick, answer: ValueFirstTapAnswer) => boolean;
  onSend: (text: string, answer: ValueFirstTapAnswer) => void;
  /** The offer row's id: a newer offer is a new question. */
  messageId?: string;
}) {
  const { t } = useTranslation();
  const click = useValueFirstClick(conversationId, VALUE_FIRST_COMPONENT.chooseGoal);
  const seenRef = useSeen(VALUE_FIRST_COMPONENT.chooseGoal, card.options.map((option) => option.id).join(','), {
    ...goalQuestionSeenFields(card),
    message_id: messageId ?? '',
  });
  const walkInPopup = useConnectWalkPopupEnabled();
  const [picked, setPicked] = useValueFirstPick(disabled);
  const pick = (option: BandOfferCardOptionData) => {
    if (disabled || picked || !onPick(bandOptionFields(option), valueFirstTapAnswer({ kind: 'goal', optionId: option.id }))) return;
    setPicked(option.id);
    if (option.connectUrl) openConnectWalk(bandConnectUrlFrom(option.connectUrl, 'web'), walkInPopup);
  };
  return (
    <Card ref={seenRef} className={styles.dock} data-testid="value-first-goal-dock">
      <div className={styles.dockHeadRow}>
        <div className={styles.dockHead}>
          <Typography variant="h4" component="h3" className={styles.dockQuestion}>
            {t('valueFirst.goal.question')}
          </Typography>
          <Typography variant="p3" color="secondary">
            {t(goalHintKey(businessFromWixPack, card))}
          </Typography>
        </div>
        <SkipButton
          disabled={disabled || picked !== null}
          onSkip={() => {
            click('value_first_skip', VALUE_FIRST_CLICKED.skip, { question: 'goal' });
            setPicked(SKIP_PICK);
            onSend(t('valueFirst.skip'), valueFirstTapAnswer({ kind: 'skip', question: 'goal' }));
          }}
        />
      </div>
      <div className={styles.offers}>
        {card.options.map((option) => {
          const state = valueFirstPickState(option.id, picked, disabled);
          return (
            <Card
              key={option.id}
              component="button"
              variant="outline"
              size="sm"
              interactive
              // Before a tap the best fit is the highlighted one; after it, only the owner's pick is.
              selected={picked ? state === 'picked' : option.bestFit}
              className={`${styles.offer}${state === 'waiting' && picked ? ` ${styles.offerWaiting}` : ''}`}
              aria-disabled={state !== 'idle' || undefined}
              aria-pressed={state === 'picked'}
              data-best-fit={option.bestFit ? 'true' : undefined}
              onClick={() => pick(option)}
            >
              {option.bestFit ? <Badge variant="recommended" label={t('valueFirst.goal.bestFit')} className={styles.bestFit} /> : null}
              <span className={styles.offerName}>{option.name}</span>
              {evidenceHasWixMark(option) ? (
                <span className={styles.offerEvidencePack}>
                  <WixMark />
                  <span>{option.evidence}</span>
                </span>
              ) : option.evidence ? (
                <span className={styles.offerEvidence}>{option.evidence}</span>
              ) : null}
              {option.goal ? <span className={styles.offerGoal}>{t('valueFirst.goal.goalLine', { goal: option.goal })}</span> : null}
              <OfferAgents option={option} />
            </Card>
          );
        })}
      </div>
      <TextButton
        size="small"
        underline
        disabled={disabled || picked !== null}
        onClick={() => {
          click('value_first_not_now', VALUE_FIRST_CLICKED.notNow);
          setPicked(NOT_NOW_PICK);
          onSend(t('valueFirst.goal.notNow'), valueFirstTapAnswer({ kind: 'notNow' }));
        }}
      >
        {t('valueFirst.goal.notNow')}
      </TextButton>
    </Card>
  );
}

/**
 * An earlier step's answer card, folded to one line once the next step's card arrived (contract `folds`): its tag, its title,
 * anything the step still needs from the owner, and a chevron that opens it again. Opening is the reader's, and local.
 */
function FoldedAnswer({
  tag,
  title,
  aside,
  onOpen,
  testId,
  cardRef,
}: {
  tag: string;
  title: string;
  aside?: ReactNode;
  onOpen: () => void;
  testId: string;
  /** The card's seen ref: a folded card is still on screen, so it still reports being seen. */
  cardRef?: Ref<HTMLElement>;
}) {
  const { t } = useTranslation();
  return (
    <Card ref={cardRef as never} component="section" className={`${styles.answer} ${styles.answerFolded}`} data-testid={testId}>
      <span className={styles.foldedCheck} aria-hidden>
        <Check size={12} />
      </span>
      <span className={styles.foldedText}>
        <span className={styles.foldedTag}>{tag}</span>
        <span className={styles.foldedTitle} dir="auto">
          {title}
        </span>
      </span>
      {aside}
      <IconButton size="small" aria-label={t('common.expand')} aria-expanded={false} onClick={onOpen}>
        <ChevronDown size={16} aria-hidden />
      </IconButton>
    </Card>
  );
}

/** The chevron on an open card that was folded: closes it back to its line. */
function FoldBack({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton size="small" className={styles.foldBack} aria-label={t('common.collapse')} aria-expanded onClick={onClose}>
      <ChevronDown size={16} aria-hidden className={styles.foldBackIcon} />
    </IconButton>
  );
}

function CardTag({ label, wixMark = false }: { label: string; wixMark?: boolean }) {
  return <Badge variant="connected" label={label} startIcon={wixMark ? <WixMark /> : <Check size={12} aria-hidden />} />;
}

/** The Wix "W" on what was read off the owner's Wix pack. A brand mark, so not a Lucide glyph. */
function WixMark() {
  return (
    <span className={styles.wixMark} aria-hidden>
      <svg viewBox="0 0 12.96 10.13" width="8" height="7" focusable="false">
        <path d="M12.9555 0H12.3406C11.6636 0 11.077 0.468172 10.927 1.12818L9.55148 7.17512L8.34751 1.53043C8.1248 0.487186 7.05437 -0.21634 5.959 0.0861978C5.2613 0.278875 4.74404 0.868316 4.59318 1.57607L3.40146 7.16751L2.02845 1.1286C1.87801 0.468595 1.29145 0 0.614452 0H0L2.30441 10.1325H3.17749C3.96985 10.1325 4.65445 9.57852 4.81969 8.80358L6.29285 1.89044C6.31102 1.80466 6.38794 1.74255 6.47541 1.74255C6.56289 1.74255 6.6398 1.80466 6.65797 1.89044L8.13241 8.804C8.29764 9.57894 8.98224 10.1325 9.77461 10.1325H10.6506L12.9555 0Z" />
      </svg>
    </span>
  );
}

/** The owner's business, as the agent understood it. Edit puts its text back in the composer. */
export function BusinessAnswerCard({
  card,
  conversationId,
  messageId,
  onEdit,
  folded = false,
}: {
  card: BusinessCardData;
  conversationId: string | null;
  messageId?: string;
  onEdit?: (text: string) => void;
  /** A later step's card has arrived: draw as one line until the reader opens it. */
  folded?: boolean;
}) {
  const { t } = useTranslation();
  const click = useValueFirstClick(conversationId, VALUE_FIRST_COMPONENT.yourBusiness);
  const copy = businessCardCopy(card);
  const [open, setOpen] = useState(false);
  const seenRef = useSeen(VALUE_FIRST_COMPONENT.yourBusiness, `${card.title}|${card.location ?? ''}|${card.source ?? ''}`, {
    ...businessCardSeenFields(card),
    message_id: messageId ?? '',
  });
  if (folded && !open) {
    return (
      <FoldedAnswer
        tag={t(copy.tagKey, { source: card.source })}
        title={copy.titleKey ? t(copy.titleKey) : card.title}
        onOpen={() => setOpen(true)}
        testId="value-first-business-card-folded"
        cardRef={seenRef as never}
      />
    );
  }
  return (
    <Card ref={seenRef} component="section" className={styles.answer} data-testid="value-first-business-card" data-wix-pack={card.fromWixPack || undefined}>
      <div className={styles.foldRow}>
        <CardTag label={t(copy.tagKey, { source: card.source })} wixMark={!!card.fromWixPack} />
        {folded ? <FoldBack onClose={() => setOpen(false)} /> : null}
      </div>
      {copy.titleKey ? (
        <Typography variant="h3" className={styles.answerTitle}>
          {t(copy.titleKey)}
        </Typography>
      ) : null}
      <div className={styles.answerHeadRow}>
        {copy.lineKey ? (
          <Typography variant="p3" color="secondary">
            {t(copy.lineKey)}
          </Typography>
        ) : (
          <div className={styles.answerHead}>
            <Typography variant="h3" className={styles.answerTitle} dir="auto">
              {card.title}
            </Typography>
            {card.location ? (
              <Typography variant="p3" color="secondary" dir="auto">
                {card.location}
              </Typography>
            ) : null}
          </div>
        )}
        {onEdit ? (
          <TextButton
            size="small"
            underline
            onClick={() => {
              click('value_first_business_edit', VALUE_FIRST_CLICKED.edit, { message_id: messageId });
              onEdit(businessCardEditText(card));
            }}
          >
            {t('valueFirst.businessCard.edit')}
          </TextButton>
        ) : null}
      </div>
      {card.details && card.details.length > 0 ? (
        <dl className={card.fromWixPack ? styles.packDetails : styles.details}>
          {card.details.map((detail) => (
            <div key={detail.label} className={styles.detail}>
              <dt>{detail.label}</dt>
              <dd dir="auto">{detail.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </Card>
  );
}

function useAgentIdByName() {
  const state = useMyAgents();
  return useMemo(() => {
    const byName = new Map<string, string>();
    if (state.status === 'ready') for (const agent of state.team.agents) byName.set(agent.name.toLowerCase(), agent.id);
    return (name: string | undefined) => (name ? byName.get(name.toLowerCase()) ?? name : 'maestro');
  }, [state]);
}

/** The first goal: its tasks with who runs each and where each stands now (contract `liveTaskStatus`), "on us", and "nothing goes out without your OK". */
export function ValueFirstGoalCard({ card, messageId, folded = false }: { card: GoalCardData; messageId?: string; folded?: boolean }) {
  const { t } = useTranslation();
  const agentId = useAgentIdByName();
  const goals = useWorkspaceGoals(undefined);
  const goal = goals.status === 'ready' ? findGoalForCard(goals.goals, card) : undefined;
  const [open, setOpen] = useState(false);
  // The live goal when the list has it: the row's snapshot says every task is working, the moment the team was made.
  const rows = goal && goal.tasks.length > 0
    ? goal.tasks.map((task) => {
        const planned = card.tasks?.find((p) => p.title === task.title);
        const state = liveTaskState({ status: task.status, awaitingDecision: awaitsDecision(task), live: showsLiveMarker(task) });
        return { key: task.id, title: task.title?.trim() || planned?.title || '', agent: planned?.agent, agentKey: task.agentId || agentId(planned?.agent), state };
      })
    : (card.tasks ?? []).map((task, i) => ({
        key: `${task.title}-${i}`,
        title: task.title,
        agent: task.agent,
        agentKey: agentId(task.agent),
        state: (task.status === 'done' ? 'done' : task.status === 'working' ? 'drafting' : 'upNext') as LiveTaskState,
      }));
  const waiting = rows.filter((row) => row.state === 'waiting').length;
  const seenRef = useSeen(VALUE_FIRST_COMPONENT.yourFirstGoal, card.goalId ?? card.title, {
    ...goalCardSeenFields(card),
    message_id: messageId ?? '',
  });
  const count = (state: LiveTaskState) => rows.filter((row) => row.state === state).length;
  const plural = (key: string, n: number) => t(`${key}.${n === 1 ? 'one' : 'other'}`, { count: n });
  // Board 6: a task waiting on the owner is the whole line; otherwise board 3's "2 drafting · 1 up next".
  const summary =
    waiting > 0 ? (
      <Badge variant="waiting-on-you" label={plural('tasks.groupStatus.waiting', waiting)} />
    ) : (
      <Typography variant="p3" color="secondary" component="span">
        {[
          [count('drafting'), 'valueFirst.goalCard.drafting'],
          [count('upNext'), 'valueFirst.goalCard.upNext'],
          [count('done'), 'tasks.groupStatus.done'],
        ]
          .filter(([n]) => (n as number) > 0)
          .map(([n, key]) => plural(key as string, n as number))
          .join(' · ')}
      </Typography>
    );
  if (folded && !open) {
    return (
      <FoldedAnswer
        tag={t('valueFirst.goalCard.tag')}
        title={goal?.title?.trim() || card.title}
        aside={
          <span className={styles.foldedAside}>
            <span className={styles.offerDots} aria-hidden>
              {rows.slice(0, 3).map((row) => (
                <span key={row.key} className={styles.offerDot}>
                  <AgentDot id={row.agentKey} size={20} />
                </span>
              ))}
            </span>
            {summary}
          </span>
        }
        onOpen={() => setOpen(true)}
        testId="value-first-goal-card-folded"
        cardRef={seenRef as never}
      />
    );
  }
  return (
    <Card ref={seenRef} component="section" className={styles.answer} data-testid="value-first-goal-card">
      <div className={styles.foldRow}>
        <CardTag label={t('valueFirst.goalCard.tag')} />
        {folded ? <FoldBack onClose={() => setOpen(false)} /> : null}
      </div>
      <div className={styles.answerHead}>
        <Typography variant="h3" className={styles.answerTitle} dir="auto">
          {goal?.title?.trim() || card.title}
        </Typography>
        {rows.length > 0 ? (
          <Typography variant="p3" color="secondary">
            {t('valueFirst.goalCard.onUs', { count: rows.length })}
          </Typography>
        ) : null}
      </div>
      {rows.length > 0 ? (
        <ul className={styles.tasks}>
          {rows.map((row) => (
            <li key={row.key} className={styles.task} data-status={row.state}>
              <AgentDot id={row.agentKey} size={20} />
              <span className={styles.taskText}>
                <span className={styles.taskTitle} dir="auto">
                  {row.title}
                </span>
                {row.agent ? <span className={styles.taskAgent}>{row.agent}</span> : null}
              </span>
              <Badge
                variant={row.state === 'drafting' ? 'in-progress' : row.state === 'done' ? 'done' : row.state === 'waiting' ? 'waiting-on-you' : 'waiting-on-a-reply'}
                label={t(LIVE_TASK_LABEL_KEYS[row.state])}
              />
            </li>
          ))}
        </ul>
      ) : null}
      <Typography variant="p3" color="secondary">
        {t('valueFirst.goalCard.nothingWithoutOk')}
      </Typography>
    </Card>
  );
}

/** The team built for the goal. */
export function TeamAnswerCard({ card, messageId, folded = false }: { card: TeamCardData; messageId?: string; folded?: boolean }) {
  const { t } = useTranslation();
  const seenRef = useSeen(VALUE_FIRST_COMPONENT.yourTeam, card.name, { ...teamCardSeenFields(card), message_id: messageId ?? '' });
  const [open, setOpen] = useState(false);
  if (folded && !open) {
    return <FoldedAnswer tag={t('valueFirst.teamCard.tag')} title={card.name} onOpen={() => setOpen(true)} testId="value-first-team-card-folded" cardRef={seenRef as never} />;
  }
  return (
    <Card ref={seenRef} component="section" className={styles.answer} data-testid="value-first-team-card" aria-label={card.name}>
      <div className={styles.foldRow}>
        <CardTag label={t('valueFirst.teamCard.tag')} />
        {folded ? <FoldBack onClose={() => setOpen(false)} /> : null}
      </div>
      <div className={styles.answerHead}>
        <Typography variant="h3" className={styles.answerTitle}>
          {t('valueFirst.teamCard.title')}
        </Typography>
        <Typography variant="p3" color="secondary">
          {t(teamCardSubtitleKey(card))}
        </Typography>
      </div>
      <div className={styles.team}>
        {card.agents.map((agent, i) => (
          <div key={`${agent.id ?? agent.name}-${i}`} className={styles.member}>
            <AgentDot id={agent.id ?? agent.name} size={28} />
            <span className={styles.memberText}>
              <span className={styles.memberName}>{agent.name}</span>
              {agent.title ? <span className={styles.memberTitle}>{agent.title}</span> : null}
            </span>
          </div>
        ))}
      </div>
      {card.connectors ? (
        <div className={styles.connectors}>
          <Typography variant="p3" color="secondary" component="span">
            {t('valueFirst.teamCard.connectors')}
          </Typography>
          <ul className={styles.connectorList}>
            {card.connectors.map((connector) => (
              <li key={connector.toolkit} className={styles.connector}>
                <span>{connector.name}</span>
                <Check size={14} aria-hidden className={styles.connectorCheck} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

/** "While they work, connect your tools": each app's Connect and Connect all open the card's one connect walk. */
export function ConnectMeanwhileAnswerCard({
  card,
  conversationId,
  messageId,
  docked = false,
}: {
  card: ConnectMeanwhileCardData;
  conversationId: string | null;
  messageId?: string;
  docked?: boolean;
}) {
  const { t } = useTranslation();
  const click = useValueFirstClick(conversationId, VALUE_FIRST_COMPONENT.connectYourTool);
  const walkInPopup = useConnectWalkPopupEnabled();
  // The same connectors read every connect card shares, so an app ticks off as the walk connects it.
  const { data: connectors } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, refetchOnWindowFocus: true });
  const connected = useMemo(() => connectedToolkitsOf(connectors?.connectors), [connectors]);
  const isConnected = (toolkit: string) => [...connected].some((c) => c.toLowerCase() === toolkit.toLowerCase());
  const seenRef = useSeen(VALUE_FIRST_COMPONENT.connectYourTool, `${card.apps.map((app) => app.toolkit).join(',')}|${docked ? 'dock' : 'chat'}`, {
    ...connectSeenFields(card, isConnected, docked),
    message_id: messageId ?? '',
  });
  const open = (buttonName: string, toolkit?: string, index = 0) => {
    click(buttonName, toolkit ? VALUE_FIRST_CLICKED.connect : VALUE_FIRST_CLICKED.connectAll, {
      connectors: toolkit ?? card.apps.map((app) => app.toolkit).join(','),
      placement: docked ? 'dock' : 'chat',
    });
    // A script-opened window, as the band card's: the walk's finish page closes the window it ran in.
    openConnectWalk(bandConnectUrlFrom(connectStepUrl(card.connectUrl, index), 'web'), walkInPopup);
  };
  const left = card.apps.filter((app) => !isConnected(app.toolkit));
  return (
    <Card
      ref={seenRef}
      component="section"
      className={docked ? styles.dock : styles.answer}
      data-testid="value-first-connect-card"
      data-docked={docked || undefined}
    >
      <div className={styles.answerHead}>
        <Typography variant="h3" className={styles.answerTitle}>
          {t('valueFirst.connect.title')}
        </Typography>
        <Typography variant="p3" color="secondary">
          {t('valueFirst.connect.subtitle')}
        </Typography>
      </div>
      <ul className={styles.apps}>
        {card.apps.map((app, index) => (
          <li key={app.toolkit} className={styles.app}>
            <img src={connectorLogoUrl(app.toolkit)} alt="" className={styles.appLogo} />
            <span className={styles.appName}>{app.name}</span>
            {isConnected(app.toolkit) ? (
              <Check size={16} aria-label={app.name} />
            ) : (
              <TextButton size="small" underline onClick={() => open('value_first_connect', app.toolkit, index)}>
                {t('valueFirst.connect.connect')}
              </TextButton>
            )}
          </li>
        ))}
      </ul>
      {left.length > 0 ? (
        <div className={styles.connectAll}>
          <Button size="small" onClick={() => open('value_first_connect_all')}>
            {t('valueFirst.connect.connectAll')}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

const confettiFired = new Set<string>();

function confettiStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The burst at `done`: once per conversation, and not at all under reduced motion. */
export function ValueFirstConfetti({ conversationId }: { conversationId: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!canvas || !claimConfetti(conversationId, confettiStorage(), confettiFired) || reduced) return;
    const timer = window.setTimeout(() => fireConfettiRain(canvas), 150);
    return () => window.clearTimeout(timer);
  }, [conversationId]);
  return <canvas ref={canvasRef} className={styles.confettiRain} aria-hidden />;
}
